import { t } from "@workspace/strings/web";
import { menuConfig } from "@/player/lib/widgets";

/**
 * Data-driven menu board: sections of name/price/description rows from the
 * widget config. Pure render — no timers, no network — so it is safe on the
 * weakest TV boxes.
 */
export function MenuWidget({
  config,
}: {
  config: Record<string, unknown> | null;
}) {
  const cfg = menuConfig(config);
  if (!cfg) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground text-[2.5vw]"
        data-testid="widget-menu"
      >
        {t("player.widgets.menu.empty")}
      </div>
    );
  }

  const columns = cfg.sections.length > 1 ? "grid-cols-2" : "grid-cols-1";

  return (
    <div
      className="flex h-full w-full flex-col bg-background px-[5vw] py-[5vh] overflow-hidden"
      data-testid="widget-menu"
    >
      {cfg.title && (
        <h1 className="mb-[4vh] text-center text-[4vw] font-semibold tracking-tight text-foreground">
          {cfg.title}
        </h1>
      )}
      <div className={`grid ${columns} flex-1 content-start gap-x-[5vw] gap-y-[4vh] overflow-hidden`}>
        {cfg.sections.map((section) => (
          <section key={section.name} className="min-w-0">
            <h2 className="mb-[1.5vh] border-b border-border pb-[1vh] text-[2.2vw] font-medium uppercase tracking-widest text-muted-foreground">
              {section.name}
            </h2>
            <ul className="space-y-[1.6vh]">
              {section.items.map((item) => (
                <li key={item.name} className="min-w-0">
                  <div className="flex items-baseline gap-[1.5vw]">
                    <span className="truncate text-[2.4vw] font-medium text-foreground">
                      {item.name}
                    </span>
                    <span className="flex-1 border-b border-dotted border-muted-foreground/30" />
                    {item.price && (
                      <span className="shrink-0 text-[2.4vw] text-foreground">
                        {item.price}
                        {cfg.currency ? ` ${cfg.currency}` : ""}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="mt-[0.4vh] truncate text-[1.6vw] text-muted-foreground">
                      {item.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
